import {
  AUTH_STORAGE_KEY,
  AUTH_STORAGE_KEY_SYNC,
  useOAuthStore,
} from '../store/oauth-store';
import { OAuthState } from '../../types';
import { isTokenForThisApp } from './access-token';

const getCookieValue = (name: string): string | null => {
  const cookies = document.cookie.split("; ").reduce(
    (acc, cookie) => {
      const [key, value] = cookie.split("=");
      acc[key] = decodeURIComponent(value);

      return acc;
    },
    {} as Record<string, string>,
  );

  return cookies[name] || null;
};

/**
 * Loads the auth state the shared cookie holds into this tab's store, unless it holds a token issued
 * to another Tinct app. Every tab reads and writes the same cookie, so it is the truth between tabs;
 * a tab's store is only its copy, refreshed by the `storage` event another tab's write fires — which
 * arrives as a task of its own, possibly after this tab already acted on the stale copy. The token
 * manager calls this directly where that matters (B-275 follow-up, token renewal).
 */
export const adoptStoredAuth = (): void => {
  try {
    const currentStoreValue = useOAuthStore.getState();
    const cookieValue = getCookieValue(AUTH_STORAGE_KEY);
    if (!cookieValue) {
      return;
    }
    const cookieState: Partial<OAuthState> | undefined = JSON.parse(cookieValue).state;
    if (!cookieState) {
      return;
    }
    if (cookieState.accessToken && !isTokenForThisApp(cookieState.accessToken)) {
      // A token minted for another Tinct app under this app's cookie name (see the store's
      // `merge`): never adopt it, and never `setState` here — the persist writer would push
      // our state back into the cookie.
      return;
    }
    if (JSON.stringify(cookieState) !== JSON.stringify(currentStoreValue)) {
      useOAuthStore.setState(cookieState);
    }
  } catch (e) {
    console.warn("Failed to sync auth-store from storage", e);
  }
};

export const registerStoreSynchronizer = () => {
  if (typeof window !== "undefined") {
    window.addEventListener("storage", (event) => {
      if (event.key === AUTH_STORAGE_KEY_SYNC && event.newValue) {
        adoptStoredAuth();
      }
    });

    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") {
        adoptStoredAuth();
      }
    });
  }
};
